import { SceneSource } from '../types/obs';
import { getIcon, getSI } from '../lib/obs-engine';
import { ICONS } from './Icon';

interface SourceCardProps {
  source: SceneSource;
  isOpen: boolean;
  onToggle: () => void;
  onUpdate: (updates: Partial<SceneSource>) => void;
  onRemove: () => void;
}

export function SourceCard({ source, isOpen, onToggle, onUpdate, onRemove }: SourceCardProps) {
  const iconType = getIcon(source.type);
  const iconClass = getSI(source.type);

  return (
    <div className={`src-card ${isOpen ? 'open' : ''}`}>
      <div className="src-card-head" onClick={onToggle}>
        <div className={`src-icon ${iconClass}`}>
          {ICONS[iconType as keyof typeof ICONS] || ICONS.screen}
        </div>
        <span className="src-card-name">{source.name}</span>
        <button
          className="src-vis-btn"
          onClick={(e) => {
            e.stopPropagation();
            onUpdate({ visible: !source.visible });
          }}
          style={{ opacity: source.visible ? 1 : 0.3 }}
        >
          {ICONS.eye}
        </button>
      </div>

      {isOpen && (
        <div className="src-card-body">
          <div className="edit-row">
            <label className="edit-label">Name</label>
            <input
              type="text"
              className="edit-input"
              value={source.name}
              onChange={(e) => onUpdate({ name: e.target.value })}
            />
          </div>

          <div className="coord-grid">
            <div className="edit-row">
              <label className="edit-label">X</label>
              <input
                type="number"
                className="edit-input"
                value={source.position.x}
                onChange={(e) => onUpdate({
                  position: { ...source.position, x: parseFloat(e.target.value) || 0 }
                })}
                step="0.01"
              />
            </div>
            <div className="edit-row">
              <label className="edit-label">Y</label>
              <input
                type="number"
                className="edit-input"
                value={source.position.y}
                onChange={(e) => onUpdate({
                  position: { ...source.position, y: parseFloat(e.target.value) || 0 }
                })}
                step="0.01"
              />
            </div>
          </div>

          <div className="coord-grid">
            <div className="edit-row">
              <label className="edit-label">Width</label>
              <input
                type="number"
                className="edit-input"
                value={source.size.w}
                onChange={(e) => onUpdate({
                  size: { ...source.size, w: parseFloat(e.target.value) || 0 }
                })}
                step="0.01"
              />
            </div>
            <div className="edit-row">
              <label className="edit-label">Height</label>
              <input
                type="number"
                className="edit-input"
                value={source.size.h}
                onChange={(e) => onUpdate({
                  size: { ...source.size, h: parseFloat(e.target.value) || 0 }
                })}
                step="0.01"
              />
            </div>
          </div>

          <div className="edit-row">
            <label className="edit-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={!!source.border?.enabled}
                onChange={(e) => onUpdate({
                  border: {
                    color: source.border?.color ?? '#FFD400',
                    thickness: source.border?.thickness ?? 8,
                    enabled: e.target.checked,
                  },
                })}
              />
              Color Border
            </label>
          </div>

          {source.border?.enabled && (
            <div className="coord-grid">
              <div className="edit-row">
                <label className="edit-label">Color</label>
                <input
                  type="color"
                  className="edit-input"
                  value={source.border.color}
                  onChange={(e) => onUpdate({
                    border: { ...source.border!, color: e.target.value },
                  })}
                />
              </div>
              <div className="edit-row">
                <label className="edit-label">Thickness (px)</label>
                <input
                  type="number"
                  className="edit-input"
                  value={source.border.thickness}
                  min={0}
                  step={1}
                  onChange={(e) => onUpdate({
                    border: { ...source.border!, thickness: parseInt(e.target.value) || 0 },
                  })}
                />
              </div>
            </div>
          )}

          <div className="action-row">
            <button className="btn btn-ghost btn-sm" onClick={onRemove}>
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
